import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { UserPlus, CheckCircle, XCircle, Clock, FolderSimple, FloppyDisk, PencilSimple, Trash, Warning, Eye, PencilLine } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const ROLES = [
  { value: 'admin', label: 'Admin', description: 'Full system access', fullAccess: true },
  { value: 'qa_manager', label: 'QA Manager', description: 'Full access to all projects', fullAccess: true },
  { value: 'inspector', label: 'Inspector', description: 'Access based on settings below', fullAccess: false },
  { value: 'operator', label: 'Operator', description: 'Access based on settings below', fullAccess: false },
  { value: 'supplier', label: 'Supplier', description: 'Access based on settings below', fullAccess: false },
  { value: 'client', label: 'Client', description: 'Access based on settings below', fullAccess: false },
  { value: 'customer', label: 'Customer', description: 'Access based on settings below', fullAccess: false },
];

const ACCESS_LEVELS = [
  { value: 'view_only', label: 'View Only', description: 'Can view projects and documents but cannot edit, upload, or download', icon: Eye },
  { value: 'edit', label: 'Edit Access', description: 'Can edit projects, upload documents, and download files', icon: PencilLine },
];

export default function UserManagementPage() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Selected user and form data
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [allProjectsAccess, setAllProjectsAccess] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [accessLevel, setAccessLevel] = useState('view_only');
  const [editName, setEditName] = useState('');
  
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [pendingRes, allRes, projectsRes] = await Promise.all([
        api.get('/users/pending'),
        api.get('/users'),
        api.get('/apqp/projects')
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
      setAllProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Check if role has full access (no need to set project access)
  const isFullAccessRole = (role) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return roleConfig?.fullAccess || false;
  };

  // Open approval modal with role and project selection
  const openApprovalModal = (pendingUser) => {
    setSelectedUser(pendingUser);
    setSelectedRole(pendingUser.role || 'inspector');
    setSelectedProjects([]);
    setAllProjectsAccess(false);
    setAccessLevel('view_only');
    setShowApprovalModal(true);
  };

  const handleApproveWithAccess = async () => {
    if (!selectedUser) return;
    
    try {
      // Determine project_ids based on selection
      let project_ids = [];
      if (!isFullAccessRole(selectedRole)) {
        if (allProjectsAccess) {
          project_ids = ['all'];
        } else {
          project_ids = selectedProjects;
        }
      }
      
      await api.patch(`/users/${selectedUser.id}/approve-with-access`, {
        role: selectedRole,
        project_ids: project_ids,
        access_level: isFullAccessRole(selectedRole) ? 'edit' : accessLevel
      });
      toast.success(`${selectedUser.name} approved as ${selectedRole.replace('_', ' ')}`);
      setShowApprovalModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Reject and delete registration for ${userName}? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User registration rejected');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  // Open edit modal for existing user
  const openEditModal = (userItem) => {
    setSelectedUser(userItem);
    setEditName(userItem.name);
    setSelectedRole(userItem.role);
    
    // Check if user has "all" projects access
    const hasAllAccess = userItem.project_access?.includes('all');
    setAllProjectsAccess(hasAllAccess);
    setSelectedProjects(hasAllAccess ? [] : (userItem.project_access || []));
    setAccessLevel(userItem.access_level || 'view_only');
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      // Determine project_ids based on selection
      let project_ids = [];
      if (!isFullAccessRole(selectedRole)) {
        if (allProjectsAccess) {
          project_ids = ['all'];
        } else {
          project_ids = selectedProjects;
        }
      }
      
      await api.put(`/users/${selectedUser.id}`, {
        name: editName,
        role: selectedRole,
        project_access: project_ids,
        access_level: isFullAccessRole(selectedRole) ? 'edit' : accessLevel
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Open project access modal (quick edit for projects only)
  const openProjectModal = (userItem) => {
    setSelectedUser(userItem);
    const hasAllAccess = userItem.project_access?.includes('all');
    setAllProjectsAccess(hasAllAccess);
    setSelectedProjects(hasAllAccess ? [] : (userItem.project_access || []));
    setAccessLevel(userItem.access_level || 'view_only');
    setShowProjectModal(true);
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSaveProjectAccess = async () => {
    if (!selectedUser) return;
    
    try {
      let project_ids = allProjectsAccess ? ['all'] : selectedProjects;
      
      await api.put(`/users/${selectedUser.id}`, {
        project_access: project_ids,
        access_level: accessLevel
      });
      toast.success('Project access updated successfully');
      setShowProjectModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project access');
    }
  };

  // Delete user
  const openDeleteConfirm = (userItem) => {
    setSelectedUser(userItem);
    setShowDeleteConfirm(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getAccessDescription = (userItem) => {
    if (isFullAccessRole(userItem.role)) {
      return 'Full Access';
    }
    const hasAllProjects = userItem.project_access?.includes('all');
    const projectCount = hasAllProjects ? 'All' : (userItem.project_access?.length || 0);
    const accessType = userItem.access_level === 'edit' ? 'Edit' : 'View';
    return `${projectCount} project(s) • ${accessType}`;
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Access Denied. Admin only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div data-testid="user-management-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">User Management</h1>
        <p className="mt-1 text-sm text-gray-600">Approve, edit, and manage user access controls</p>
      </div>

      {/* Pending Registrations */}
      <div className="bg-white border border-gray-200 rounded-sm">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <Clock size={24} weight="duotone" className="text-orange-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pending Approvals</h2>
              <p className="text-sm text-gray-600">{pendingUsers.length} registration(s) awaiting approval</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {pendingUsers.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No pending registrations</p>
          ) : (
            <div className="space-y-3">
              {pendingUsers.map((pendingUser, idx) => (
                <div
                  key={pendingUser.id}
                  data-testid={`pending-user-${idx}`}
                  className="flex items-center justify-between p-4 bg-orange-50 border border-orange-200 rounded-sm"
                >
                  <div>
                    <p className="font-semibold text-gray-900">{pendingUser.name}</p>
                    <p className="text-sm text-gray-600">{pendingUser.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Requested Role: <span className="font-medium capitalize">{pendingUser.role.replace('_', ' ')}</span> • 
                      Registered: {new Date(pendingUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`approve-user-${idx}`}
                      onClick={() => openApprovalModal(pendingUser)}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      <CheckCircle size={18} weight="bold" /> Approve & Set Access
                    </button>
                    <button
                      data-testid={`reject-user-${idx}`}
                      onClick={() => handleReject(pendingUser.id, pendingUser.name)}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      <XCircle size={18} weight="bold" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* All Users */}
      <div className="bg-white border border-gray-200 rounded-sm">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <UserPlus size={24} weight="duotone" className="text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
              <p className="text-sm text-gray-600">{allUsers.length} total user(s)</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Access</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {allUsers.map((u, idx) => {
                const isPrimaryAdmin = u.email === user?.email && u.role === 'admin';
                return (
                  <tr key={u.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-sm capitalize ${
                        u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        u.role === 'qa_manager' ? 'bg-blue-100 text-blue-700' :
                        u.role === 'customer' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                        u.approved ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {u.approved ? 'APPROVED' : 'PENDING'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {isFullAccessRole(u.role) ? (
                        <span className="text-xs text-gray-500 italic">Full Access</span>
                      ) : (
                        <button
                          data-testid={`manage-access-${idx}`}
                          onClick={() => openProjectModal(u)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          <FolderSimple size={16} />
                          {getAccessDescription(u)}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`edit-user-${idx}`}
                          onClick={() => openEditModal(u)}
                          className="flex items-center gap-1 text-gray-600 hover:text-gray-800 text-sm font-medium"
                          title="Edit User"
                        >
                          <PencilSimple size={16} /> Edit
                        </button>
                        {!isPrimaryAdmin && (
                          <button
                            data-testid={`delete-user-${idx}`}
                            onClick={() => openDeleteConfirm(u)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
                            title="Delete User"
                          >
                            <Trash size={16} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Approval Modal with Role and Project Selection */}
      {showApprovalModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-200 p-6 bg-green-600">
              <h2 className="text-xl font-semibold text-white">Approve User Registration</h2>
              <p className="text-sm text-green-100 mt-1">
                Set role and access for <span className="font-medium">{selectedUser.name}</span>
              </p>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-sm p-4">
                <p className="text-sm text-gray-600">Email: <span className="font-medium text-gray-900">{selectedUser.email}</span></p>
                <p className="text-sm text-gray-600 mt-1">Requested Role: <span className="font-medium text-gray-900 capitalize">{selectedUser.role.replace('_', ' ')}</span></p>
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                  Assign Role
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map((role) => (
                    <label
                      key={role.value}
                      className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                        selectedRole === role.value
                          ? 'bg-green-50 border-green-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={selectedRole === role.value}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="mt-1 w-4 h-4 text-green-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{role.label}</p>
                        <p className="text-xs text-gray-500">{role.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Access Settings (only for non-full-access roles) */}
              {!isFullAccessRole(selectedRole) && (
                <>
                  {/* Access Level Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Access Level
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {ACCESS_LEVELS.map((level) => {
                        const Icon = level.icon;
                        return (
                          <label
                            key={level.value}
                            className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                              accessLevel === level.value
                                ? level.value === 'edit' ? 'bg-blue-50 border-blue-300' : 'bg-amber-50 border-amber-300'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="access_level"
                              value={level.value}
                              checked={accessLevel === level.value}
                              onChange={(e) => setAccessLevel(e.target.value)}
                              className="mt-1 w-4 h-4 text-blue-600"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Icon size={18} className={level.value === 'edit' ? 'text-blue-600' : 'text-amber-600'} />
                                <p className="text-sm font-medium text-gray-900">{level.label}</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{level.description}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Project Access */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Project Access
                    </label>
                    
                    {/* All Projects Toggle */}
                    <label className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-colors mb-3 ${
                      allProjectsAccess ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={allProjectsAccess}
                        onChange={(e) => {
                          setAllProjectsAccess(e.target.checked);
                          if (e.target.checked) setSelectedProjects([]);
                        }}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">All Projects</p>
                        <p className="text-xs text-gray-500">Grant access to all current and future projects</p>
                      </div>
                    </label>

                    {/* Individual Project Selection */}
                    {!allProjectsAccess && (
                      <>
                        <p className="text-xs text-gray-500 mb-2">Or select specific projects:</p>
                        {allProjects.length === 0 ? (
                          <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-sm">No projects available</p>
                        ) : (
                          <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-sm p-2">
                            {allProjects.map((project) => (
                              <label
                                key={project.id}
                                className={`flex items-center gap-3 p-2 border rounded-sm cursor-pointer transition-colors ${
                                  selectedProjects.includes(project.id)
                                    ? 'bg-blue-50 border-blue-300'
                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.includes(project.id)}
                                  onChange={() => handleProjectToggle(project.id)}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{project.project_name}</p>
                                  <p className="text-xs text-gray-500">{project.customer} - {project.part_number}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowApprovalModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="confirm-approve-user"
                  onClick={handleApproveWithAccess}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors"
                >
                  <CheckCircle size={18} weight="bold" />
                  Approve User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-200 p-6 bg-gray-900">
              <h2 className="text-xl font-semibold text-white">Edit User</h2>
              <p className="text-sm text-gray-300 mt-1">
                Update access controls for <span className="font-medium">{selectedUser.email}</span>
              </p>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                  Role
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map((role) => (
                    <label
                      key={role.value}
                      className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                        selectedRole === role.value
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="edit-role"
                        value={role.value}
                        checked={selectedRole === role.value}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="mt-1 w-4 h-4 text-blue-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{role.label}</p>
                        <p className="text-xs text-gray-500">{role.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Access Settings (only for non-full-access roles) */}
              {!isFullAccessRole(selectedRole) && (
                <>
                  {/* Access Level Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Access Level
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {ACCESS_LEVELS.map((level) => {
                        const Icon = level.icon;
                        return (
                          <label
                            key={level.value}
                            className={`flex items-start gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                              accessLevel === level.value
                                ? level.value === 'edit' ? 'bg-blue-50 border-blue-300' : 'bg-amber-50 border-amber-300'
                                : 'bg-white border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name="edit-access_level"
                              value={level.value}
                              checked={accessLevel === level.value}
                              onChange={(e) => setAccessLevel(e.target.value)}
                              className="mt-1 w-4 h-4 text-blue-600"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Icon size={18} className={level.value === 'edit' ? 'text-blue-600' : 'text-amber-600'} />
                                <p className="text-sm font-medium text-gray-900">{level.label}</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{level.description}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Project Access */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
                      Project Access
                    </label>
                    
                    {/* All Projects Toggle */}
                    <label className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-colors mb-3 ${
                      allProjectsAccess ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={allProjectsAccess}
                        onChange={(e) => {
                          setAllProjectsAccess(e.target.checked);
                          if (e.target.checked) setSelectedProjects([]);
                        }}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">All Projects</p>
                        <p className="text-xs text-gray-500">Grant access to all current and future projects</p>
                      </div>
                    </label>

                    {/* Individual Project Selection */}
                    {!allProjectsAccess && (
                      <>
                        <p className="text-xs text-gray-500 mb-2">Or select specific projects:</p>
                        {allProjects.length === 0 ? (
                          <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-sm">No projects available</p>
                        ) : (
                          <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-sm p-2">
                            {allProjects.map((project) => (
                              <label
                                key={project.id}
                                className={`flex items-center gap-3 p-2 border rounded-sm cursor-pointer transition-colors ${
                                  selectedProjects.includes(project.id)
                                    ? 'bg-blue-50 border-blue-300'
                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.includes(project.id)}
                                  onChange={() => handleProjectToggle(project.id)}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{project.project_name}</p>
                                  <p className="text-xs text-gray-500">{project.customer} - {project.part_number}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="save-user-edit"
                  onClick={handleUpdateUser}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  <FloppyDisk size={18} weight="bold" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Access Modal (Quick Edit) */}
      {showProjectModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Manage Access</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure access for <span className="font-medium">{selectedUser.name}</span>
              </p>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Access Level */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 uppercase tracking-wider mb-2">
                  Access Level
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {ACCESS_LEVELS.map((level) => {
                    const Icon = level.icon;
                    return (
                      <label
                        key={level.value}
                        className={`flex items-center gap-2 p-3 border rounded-sm cursor-pointer transition-colors ${
                          accessLevel === level.value
                            ? level.value === 'edit' ? 'bg-blue-50 border-blue-300' : 'bg-amber-50 border-amber-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="quick-access_level"
                          value={level.value}
                          checked={accessLevel === level.value}
                          onChange={(e) => setAccessLevel(e.target.value)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <Icon size={18} className={level.value === 'edit' ? 'text-blue-600' : 'text-amber-600'} />
                        <span className="text-sm font-medium text-gray-900">{level.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* All Projects Toggle */}
              <label className={`flex items-center gap-3 p-4 border rounded-sm cursor-pointer transition-colors ${
                allProjectsAccess ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200 hover:bg-gray-50'
              }`}>
                <input
                  type="checkbox"
                  checked={allProjectsAccess}
                  onChange={(e) => {
                    setAllProjectsAccess(e.target.checked);
                    if (e.target.checked) setSelectedProjects([]);
                  }}
                  className="w-4 h-4 text-green-600 rounded"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">All Projects</p>
                  <p className="text-xs text-gray-500">Access to all current and future projects</p>
                </div>
              </label>

              {/* Individual Project Selection */}
              {!allProjectsAccess && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Or select specific projects:</p>
                  {allProjects.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No projects available</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {allProjects.map((project) => (
                        <label
                          key={project.id}
                          className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                            selectedProjects.includes(project.id)
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedProjects.includes(project.id)}
                            onChange={() => handleProjectToggle(project.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{project.project_name}</p>
                            <p className="text-xs text-gray-500">{project.customer} - {project.part_number}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowProjectModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="save-project-access"
                  onClick={handleSaveProjectAccess}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  <FloppyDisk size={18} weight="bold" />
                  Save Access
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Warning size={24} className="text-red-600" weight="fill" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Delete User</h2>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-4">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete <span className="font-semibold">{selectedUser.name}</span> ({selectedUser.email})?
                </p>
                <p className="text-xs text-red-600 mt-2">
                  All access and data associated with this user will be permanently removed.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="confirm-delete-user"
                  onClick={handleDeleteUser}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
                >
                  <Trash size={18} weight="bold" />
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
